import * as Sentry from '@sentry/react'
import {
  addDatadogAction,
  addDatadogError,
  setDatadogUser,
} from './datadog'

type LogLevel = 'debug' | 'info' | 'warn' | 'error'
type LogContext = Record<string, unknown>

const CLIENT_NAME = 'justlockit-client'
const REDACTED = '[REDACTED]'
const MAX_DEPTH = 5
const API_PATH_PREFIXES = [
  '/admin',
  '/payments',
  '/portal',
  '/profiles',
  '/report',
  '/shippings',
  '/subscriptions',
  '/transactions',
]
const SENSITIVE_KEY_PATTERN =
  /(authorization|token|password|secret|api[-_]?key|card|cvv|cvc|routing|account|cookie|session|firebase|credit|bank|ssn|email|phone|name)/i

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  Object.prototype.toString.call(value) === '[object Object]'

const sanitizeLogValue = (value: unknown, key = '', depth = 0): unknown => {
  if (depth >= MAX_DEPTH) {
    return '[Truncated]'
  }

  if (key && SENSITIVE_KEY_PATTERN.test(key)) {
    return REDACTED
  }

  if (
    value == null ||
    typeof value === 'number' ||
    typeof value === 'boolean' ||
    typeof value === 'bigint'
  ) {
    return value
  }

  if (typeof value === 'string') {
    return value.length > 500 ? `${value.slice(0, 497)}...` : value
  }

  if (value instanceof Error) {
    return {
      name: value.name,
      message: value.message,
      stack: value.stack,
    }
  }

  if (Array.isArray(value)) {
    return value.map((entry) => sanitizeLogValue(entry, key, depth + 1))
  }

  if (isPlainObject(value)) {
    return Object.entries(value).reduce<Record<string, unknown>>(
      (accumulator, [entryKey, entryValue]) => {
        accumulator[entryKey] = sanitizeLogValue(
          entryValue,
          entryKey,
          depth + 1
        )
        return accumulator
      },
      {}
    )
  }

  return String(value)
}

export const sanitizeLogContext = (
  context: LogContext | undefined
): LogContext | undefined => {
  if (!context) {
    return undefined
  }

  return sanitizeLogValue(context) as LogContext
}

const emitConsoleLog = (
  level: Exclude<LogLevel, 'debug'>,
  message: string,
  context?: LogContext
) => {
  if (!import.meta.env.DEV) {
    return
  }

  const consoleMethod =
    level === 'error' ? console.error : level === 'warn' ? console.warn : console.info

  consoleMethod(`[${level.toUpperCase()}] ${message}`, context ?? {})
}

const log = (level: LogLevel, message: string, context?: LogContext) => {
  const attributes = sanitizeLogContext({
    ...context,
    app: CLIENT_NAME,
    path: window.location.pathname,
  })

  if (Sentry.isInitialized()) {
    switch (level) {
      case 'debug':
        Sentry.logger.debug(message, attributes)
        break
      case 'info':
        Sentry.logger.info(message, attributes)
        emitConsoleLog('info', message, attributes)
        break
      case 'warn':
        Sentry.logger.warn(message, attributes)
        emitConsoleLog('warn', message, attributes)
        break
      case 'error':
        Sentry.logger.error(message, attributes)
        emitConsoleLog('error', message, attributes)
        break
    }
    return
  }

  if (level !== 'debug') {
    emitConsoleLog(level, message, attributes)
  }
}

const isTrackedApiRequest = (rawUrl: string) => {
  try {
    const parsedUrl = new URL(rawUrl, window.location.origin)

    return API_PATH_PREFIXES.some((prefix) => parsedUrl.pathname.startsWith(prefix))
  } catch {
    return false
  }
}

const getFetchMethod = (input: RequestInfo | URL, init?: RequestInit) => {
  if (init?.method) {
    return init.method.toUpperCase()
  }

  if (typeof Request !== 'undefined' && input instanceof Request) {
    return input.method.toUpperCase()
  }

  return 'GET'
}

const getFetchUrl = (input: RequestInfo | URL) => {
  if (typeof input === 'string') {
    return input
  }

  if (input instanceof URL) {
    return input.toString()
  }

  return input.url
}

export const clientLogger = {
  debug: (message: string, context?: LogContext) => log('debug', message, context),
  info: (message: string, context?: LogContext) => log('info', message, context),
  warn: (message: string, context?: LogContext) => log('warn', message, context),
  error: (message: string, context?: LogContext) => {
    const attributes = sanitizeLogContext(context)

    log('error', message, attributes)
    addDatadogError(message, context?.error, attributes)
  },
  action: (name: string, context?: LogContext) => {
    const attributes = sanitizeLogContext(context)

    log('info', `action.${name}`, attributes)
    addDatadogAction(name, attributes)
  },
  setUser: (
    user:
      | {
          uid: string
          displayName?: string | null
          providerId?: string | null
        }
      | null
      | undefined
  ) => {
    if (!user) {
      Sentry.setUser(null)
      setDatadogUser(null)
      return
    }

    Sentry.setUser({
      id: user.uid,
      username: user.displayName ?? undefined,
    })
    Sentry.setTag('auth.provider', user.providerId ?? 'firebase')
    setDatadogUser(user)
  },
}

export const instrumentClientFetch = () => {
  if (window.__JUSTLOCKIT_FETCH_INSTRUMENTED__) {
    return
  }

  const originalFetch = window.fetch.bind(window)

  window.fetch = (async (input: RequestInfo | URL, init?: RequestInit) => {
    const startedAt = performance.now()
    const url = getFetchUrl(input)

    if (!isTrackedApiRequest(url)) {
      return originalFetch(input, init)
    }

    const method = getFetchMethod(input, init)
    const parsedUrl = new URL(url, window.location.origin)

    try {
      const response = await originalFetch(input, init)
      const durationMs = Math.round(performance.now() - startedAt)
      const context = {
        method,
        status: response.status,
        ok: response.ok,
        durationMs,
        pathname: parsedUrl.pathname,
      }

      if (response.ok) {
        clientLogger.debug('api.response', context)
      } else {
        clientLogger.warn('api.response', context)
      }

      return response
    } catch (error) {
      clientLogger.error('api.failure', {
        method,
        pathname: parsedUrl.pathname,
        durationMs: Math.round(performance.now() - startedAt),
        error,
      })
      throw error
    }
  }) as typeof window.fetch

  window.__JUSTLOCKIT_FETCH_INSTRUMENTED__ = true
}

declare global {
  interface Window {
    __JUSTLOCKIT_FETCH_INSTRUMENTED__?: boolean
  }
}
